import {
  boolean,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uuid,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

// ---------------------------------------------------------------------------
// Social Platforms
// ---------------------------------------------------------------------------

export const SOCIAL_PLATFORMS = ["facebook", "instagram", "linkedin"] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

// ---------------------------------------------------------------------------
// Social Post Statuses
// ---------------------------------------------------------------------------

export const SOCIAL_POST_STATUSES = ["draft", "scheduled", "publishing", "published", "partially_failed", "failed"] as const;
export type SocialPostStatus = (typeof SOCIAL_POST_STATUSES)[number];

export const SOCIAL_TARGET_STATUSES = ["draft", "scheduled", "publishing", "published", "failed"] as const;
export type SocialTargetStatus = (typeof SOCIAL_TARGET_STATUSES)[number];

// Approval workflow (SA-D) — gates the move from draft → scheduled when an
// org requires an approver. SOC-005 exits PARTIAL once this state machine
// is enforced for every social post.
export const SOCIAL_POST_APPROVAL_STATUSES = ["draft", "pending_approval", "approved", "rejected"] as const;
export type SocialPostApprovalStatus = (typeof SOCIAL_POST_APPROVAL_STATUSES)[number];

// ---------------------------------------------------------------------------
// Social Accounts — connected FB Page / IG Business / LinkedIn Company
// ---------------------------------------------------------------------------

export const socialAccounts = pgTable("social_accounts", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  platform: text("platform").notNull(),
  externalAccountId: text("external_account_id").notNull(),
  accountName: text("account_name").notNull(),
  oauthIntegrationId: uuid("oauth_integration_id"),
  followerCount: integer("follower_count"),
  isActive: boolean("is_active").notNull().default(true),
  metadata: jsonb("metadata").default({}).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_social_accounts_tenant_platform").on(table.tenantId, table.platform, table.externalAccountId),
  index("idx_social_accounts_tenant").on(table.tenantId),
]);

// ---------------------------------------------------------------------------
// Social Posts — content to publish
// ---------------------------------------------------------------------------

/**
 * SOC-005 — source artifact attribution columns.
 *
 * Pinned to the Phase 2 vocabulary in
 * `packages/db/src/schema/soc-005-source-artifact-event-catalog.ts`:
 *
 *  - `source_artifact_type` is one of `SOC_005_SOURCE_ARTIFACT_TYPES`
 *    (review / blog_post / page / campaign / manual). Application code is
 *    expected to validate writes against that closed catalog before insert.
 *  - `source_artifact_id` is null when type === "manual" (catch-all for
 *    hand-authored drafts) and a uuid otherwise.
 *  - `source_artifact_excerpt` is an immutable snapshot of the cited text
 *    at draft time, capped at 1000 chars so a later edit to the source
 *    review/blog cannot retroactively re-shape what the draft was
 *    attributed to.
 */
export const SOC_005_SOURCE_ARTIFACT_EXCERPT_MAX_LENGTH = 1000;

export const socialPosts = pgTable("social_posts", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  authorUserId: text("author_user_id").notNull(),
  body: text("body").notNull(),
  mediaR2Keys: jsonb("media_r2_keys").$type<string[]>().default([]).notNull(),
  scheduledAt: timestamp("scheduled_at", { withTimezone: true }),
  isEnqueued: boolean("is_enqueued").notNull().default(false),
  // SA-D approval state machine. Defaults to "draft"; requesters move to
  // "pending_approval", approvers move to "approved" or "rejected".
  approvalStatus: text("approval_status").$type<SocialPostApprovalStatus>().notNull().default("draft"),
  approvalRequestedBy: text("approval_requested_by"),
  approvalRequestedAt: timestamp("approval_requested_at", { withTimezone: true }),
  approvedBy: text("approved_by"),
  approvedAt: timestamp("approved_at", { withTimezone: true }),
  rejectionReason: text("rejection_reason"),
  // SOC-005 source-artifact attribution (Phase 3). See header comment above.
  sourceArtifactType: text("source_artifact_type"),
  sourceArtifactId: uuid("source_artifact_id"),
  sourceArtifactExcerpt: text("source_artifact_excerpt"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_social_posts_tenant").on(table.tenantId),
  index("idx_social_posts_scheduled").on(table.tenantId, table.scheduledAt),
  index("idx_social_posts_approval").on(table.tenantId, table.approvalStatus),
  index("idx_social_posts_source_artifact").on(table.tenantId, table.sourceArtifactType, table.sourceArtifactId),
]);

// ---------------------------------------------------------------------------
// Social Post Targets — per-platform result for each post
// ---------------------------------------------------------------------------

export interface EngagementSnapshot {
  likes?: number;
  comments?: number;
  shares?: number;
  reach?: number;
  impressions?: number;
  saves?: number;
  updatedAt?: string;
}

export const socialPostTargets = pgTable("social_post_targets", {
  id: uuid("id").primaryKey().defaultRandom(),
  postId: uuid("post_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  socialAccountId: uuid("social_account_id").notNull(),
  platform: text("platform").notNull(),
  status: text("status").notNull().default("draft"),
  externalPostId: text("external_post_id"),
  externalContainerId: text("external_container_id"), // Instagram async publish
  containerCheckCount: integer("container_check_count").notNull().default(0),
  publishedAt: timestamp("published_at", { withTimezone: true }),
  failedAt: timestamp("failed_at", { withTimezone: true }),
  failureReason: text("failure_reason"),
  engagementSnapshot: jsonb("engagement_snapshot").$type<EngagementSnapshot>().default({}).notNull(),
  lastMetricsPollAt: timestamp("last_metrics_poll_at", { withTimezone: true }),
  contentType: text("content_type"), // text, image, video, carousel
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_social_targets_post").on(table.postId),
  index("idx_social_targets_tenant").on(table.tenantId, table.platform),
  index("idx_social_targets_status").on(table.tenantId, table.status),
  index("idx_social_targets_metrics").on(table.tenantId, table.publishedAt, table.lastMetricsPollAt),
]);

// ---------------------------------------------------------------------------
// Social Comments — ingested from platform APIs / webhooks
// ---------------------------------------------------------------------------

export const socialComments = pgTable("social_comments", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  postTargetId: uuid("post_target_id").notNull(),
  externalCommentId: text("external_comment_id").notNull(),
  authorName: text("author_name"),
  authorExternalId: text("author_external_id"),
  body: text("body"),
  parentCommentId: uuid("parent_comment_id"), // for threaded replies
  isPublicData: boolean("is_public_data").notNull().default(true),
  platform: text("platform").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_social_comments_external").on(table.tenantId, table.platform, table.externalCommentId),
  index("idx_social_comments_post").on(table.postTargetId),
  index("idx_social_comments_tenant").on(table.tenantId),
]);

// ---------------------------------------------------------------------------
// Social Automation Rules — keyword triggers + actions
// ---------------------------------------------------------------------------

export const AUTOMATION_TRIGGER_TYPES = ["keyword_dm", "keyword_comment", "new_follower"] as const;
export type AutomationTriggerType = (typeof AUTOMATION_TRIGGER_TYPES)[number];

export const AUTOMATION_ACTION_TYPES = ["auto_reply_dm", "auto_reply_comment", "trigger_workflow", "tag_contact"] as const;
export type AutomationActionType = (typeof AUTOMATION_ACTION_TYPES)[number];

export const KEYWORD_MATCH_MODES = ["contains", "exact", "starts_with", "ends_with", "regex"] as const;
export type KeywordMatchMode = (typeof KEYWORD_MATCH_MODES)[number];

export interface AutomationTriggerConfig {
  keywords?: string[];
  matchMode?: KeywordMatchMode;
  platforms?: SocialPlatform[];
}

export interface AutomationActionConfig {
  replyText?: string;
  workflowId?: string;
  tagName?: string;
  templateId?: string; // WhatsApp template fallback for expired windows
}

export const socialAutomationRules = pgTable("social_automation_rules", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  name: text("name").notNull(),
  isActive: boolean("is_active").notNull().default(true),
  triggerType: text("trigger_type").notNull(),
  triggerConfig: jsonb("trigger_config").$type<AutomationTriggerConfig>().default({}).notNull(),
  actionType: text("action_type").notNull(),
  actionConfig: jsonb("action_config").$type<AutomationActionConfig>().default({}).notNull(),
  priority: integer("priority").notNull().default(0),
  maxFiresPerDay: integer("max_fires_per_day").notNull().default(100),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_automation_rules_tenant").on(table.tenantId, table.isActive),
  index("idx_automation_rules_priority").on(table.tenantId, table.priority),
]);

// ---------------------------------------------------------------------------
// Messaging Windows — generic (replaces WhatsApp-only pattern)
// ---------------------------------------------------------------------------

export const messagingWindows = pgTable("messaging_windows", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  personId: uuid("person_id").notNull(),
  channel: text("channel").notNull(),
  lastInboundAt: timestamp("last_inbound_at", { withTimezone: true }).notNull(),
  windowExpiresAt: timestamp("window_expires_at", { withTimezone: true }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_messaging_windows").on(table.tenantId, table.personId, table.channel),
  index("idx_messaging_windows_expires").on(table.tenantId, table.channel, table.windowExpiresAt),
]);

// ---------------------------------------------------------------------------
// Social Listening — persisted monitoring feed + polling checkpoints
// ---------------------------------------------------------------------------

export const socialListeningMentions = pgTable("social_listening_mentions", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  platform: text("platform").notNull(),
  externalMentionId: text("external_mention_id").notNull(),
  authorName: text("author_name"),
  authorHandle: text("author_handle"),
  content: text("content").notNull(),
  url: text("url"),
  sentiment: text("sentiment").notNull().default("neutral"),
  matchedKeywords: jsonb("matched_keywords").$type<string[]>().notNull().default([]),
  metadata: jsonb("metadata").$type<Record<string, unknown>>().notNull().default({}),
  publishedAt: timestamp("published_at", { withTimezone: true }).notNull(),
  lastPolledAt: timestamp("last_polled_at", { withTimezone: true }).notNull().defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_social_listening_mentions_external").on(table.tenantId, table.platform, table.externalMentionId),
  index("idx_social_listening_mentions_tenant_published").on(table.tenantId, table.publishedAt),
  index("idx_social_listening_mentions_tenant_sentiment").on(table.tenantId, table.sentiment, table.publishedAt),
]);

export const socialListeningPollStates = pgTable("social_listening_poll_states", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  platform: text("platform").notNull(),
  lastPolledAt: timestamp("last_polled_at", { withTimezone: true }),
  lastSuccessfulPollAt: timestamp("last_successful_poll_at", { withTimezone: true }),
  lastError: text("last_error"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_social_listening_poll_states_platform").on(table.tenantId, table.platform),
  index("idx_social_listening_poll_states_tenant").on(table.tenantId, table.platform, table.lastPolledAt),
]);
